class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetButton = document.querySelector(".facets-button-show");
    this.productGrid = document.getElementById("ProductGridContainer");
    this.facetForm = this.querySelector("form");
    this.facetSubmitButton = this.querySelector(".facets__submit");

    this.facetsOverlay = this.querySelector(".facets__overlay");
    this.facetWrapper = this.querySelector("#FacetsWrapperDesktop");

    this.facetForm?.addEventListener(
      "input",
      this.debouncedOnSubmit.bind(this)
    );
    this.facetSubmitButton?.addEventListener(
      "click",
      this.debouncedOnSubmit.bind(this)
    );

    if (this.classList.contains("facets--desktop")) {
      this.facetWrapper?.addEventListener(
        "keydown",
        this.onHandleKeyDown.bind(this)
      );
      // §89 QA fix r1 (UX r5 P2): on collections, Tab and Shift+Tab wrap
      // between the first and last drawer controls that are on screen. The
      // theme's trapFocus() keeps the list it read when the drawer opened,
      // and that list ends on "Clear filters", which the collection drawer
      // hides while nothing is set, so Tab from "Show items" left the drawer
      // and Shift+Tab from Close went nowhere.
      if (document.body.classList.contains("template-collection")) {
        this.facetWrapper?.addEventListener("keydown", (event) => {
          if (event.key !== "Tab") return;
          const open = this.facetForm?.querySelector(".facets__container.active");
          if (!open) return;
          const els = getFocusableElements(this.facetWrapper).filter(
            (el) =>
              el.getClientRects().length &&
              getComputedStyle(el).visibility !== "hidden"
          );
          if (!els.length) return;
          const first = els[0];
          const last = els[els.length - 1];
          const at = document.activeElement;
          if (!event.shiftKey && at === last) {
            event.preventDefault();
            event.stopPropagation();
            first.focus();
          } else if (
            event.shiftKey &&
            (at === first || at === this.facetWrapper)
          ) {
            event.preventDefault();
            event.stopPropagation();
            last.focus();
          }
        });
      }
      this.facetButton?.addEventListener(
        "click",
        this.handleFacetButtonClick.bind(this)
      );
      this.facetButton?.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") {
          this.handleFacetButtonClick(e);
        }
      });

      this.addEventListener("click", (event) => {
        const btn = event.target.closest(".facets-modal__close");
        if (btn) {
          event.preventDefault();
          this.closeFacets(event);

          // §89 r5: on collections closeFacets has already returned focus
          // to the button that opened the drawer
          if (
            btn.classList.contains("modal-close-button") &&
            !document.body.classList.contains("template-collection")
          ) {
            const facetOpenBtn = document.querySelector(".facets-button-show");
            if (facetOpenBtn) {
              // §89 r3: the button is in the sticky bar, already in view;
              // focusing it must not nudge the page (it moved 6-10px)
              facetOpenBtn.focus({ preventScroll: true });
            } else {
              document.querySelector(".js-per-row__button")?.focus();
            }
          }
        }
      });

      this.facetsOverlay?.addEventListener(
        "click",
        this.closeFacets.bind(this)
      );
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        const container = this.facetForm?.querySelector(".facets__container");
        if (container && container.classList.contains("active")) {
          this.closeFacets(event);
        }
      }
    });

    if (this.querySelector(".facet-sort-select")) {
      this.initCustomSortSelect();
    }
    this.initFilterButtons();

    FacetFiltersForm.initAccessibleFacetCheckboxes();
    FacetFiltersForm.handleHeroFilters();
  }

  initFilterButtons() {
    const buttons = document.querySelectorAll(".facet-open-button");
    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const filterIndex = btn.dataset.filterIndex;
        if (!filterIndex) return;

        this.handleFacetButtonClick(new Event("click"));

        // §89 r3: groups the section marks data-mg-open (a short drawer,
        // three groups or fewer) stay open; the rest close as before
        this.querySelectorAll("details.js-filter").forEach((detail) => {
          detail.open = detail.hasAttribute("data-mg-open");
        });

        const targetDetail = this.querySelector(
          `details.js-filter[data-index="${filterIndex}"]`
        );
        if (targetDetail) {
          targetDetail.open = true;
          targetDetail.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
    const buttonAll = document.querySelector(".facets-button-show");
    buttonAll?.addEventListener("click", () => {
      this.handleFacetButtonClick(new Event("click"));
      this.querySelectorAll("details.js-filter").forEach((detail) => {
        detail.open = detail.hasAttribute("data-mg-open");
      });
    });
  }

  static initAccessibleFacetCheckboxes() {
    document.querySelectorAll("label.facet-checkbox").forEach((label) => {
      if (label.dataset.accessibleInit) return;
      label.dataset.accessibleInit = "true";

      label.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          const input = document.getElementById(label.getAttribute("for"));
          if (!input || input.disabled) return;

          input.checked = !input.checked;
          input.dispatchEvent(new Event("input", { bubbles: true }));

          label.setAttribute("aria-checked", input.checked);
        }
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );

    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.add("disabled");
    });

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;
    if (
      document.querySelector(".js-load-more") ||
      document.querySelector(".js-infinite-scroll")
    ) {
      initLoadMore();
    }
  }

  static renderProductCount(html) {
    const count =
      new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById("ProductCount")?.innerHTML || "";

    // §89 r4: a template without #ProductCount (search, sort only) writes into a detached span
    const container = document.getElementById("ProductCount") || document.createElement("span");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    container.innerHTML = count;
    container.classList.remove("loading");

    // §89 r2: keep "All filters (n)" in step with the applied filters
    const mgCount = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(".mg-filter-count");
    document.querySelectorAll(".mg-filter-count").forEach((el) => {
      el.innerHTML = mgCount ? mgCount.innerHTML : "";
    });

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderShowItems(parsedHTML);

    if (countsToRender) {
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter")
      );
    }
    FacetFiltersForm.handleHeroFilters();

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.remove("disabled");
    });

    FacetFiltersForm.initAccessibleFacetCheckboxes();
    const modal = document.getElementById("FacetsWrapperDesktop");
    //if (modal) trapFocus(modal);

    setTimeout(() => {
      // §89 r4: no filter drawer on the page (search with sorting only): nothing to focus or trap
      if (!modal) return;
      let elToFocus = null;

      if (event?.target) {
        const currentFilter = event.target.closest(".js-filter");
        if (currentFilter) {
          const activeElement =
            currentFilter.querySelector(":focus") || event.target;
          elToFocus = activeElement;
        }
      }

      if (!elToFocus && this.lastFocusedIndex) {
        const filterByIndex = modal.querySelector(
          `.js-filter[data-index="${this.lastFocusedIndex}"]`
        );
        if (filterByIndex) {
          elToFocus = filterByIndex.querySelector("input, button");
        }
      }

      if (!elToFocus) {
        elToFocus = modal.querySelector(".js-filter input, .js-filter button");
      }

      trapFocus(modal, elToFocus || undefined);

      // §89 QA fix r1: focus back on the bar after a chip removal (above)
      const mgRefocus = FacetFiltersForm.mgRefocus;
      if (mgRefocus) {
        FacetFiltersForm.mgRefocus = null;
        const shown = (el) => el && el.getClientRects().length;
        const chips = [
          ...document.querySelectorAll(".facets__top a.active-facets__button"),
        ].filter(shown);
        const to =
          chips[Math.min(mgRefocus.index, chips.length - 1)] ||
          [...document.querySelectorAll(".facets-button-show")].find(shown);
        to?.focus({ preventScroll: true });
      }
    }, 0);
  }

  //static renderActiveFacets(html) {
  //  const activeFacetsElement = html.querySelector(".active-facets-desktop");
  //  if (!activeFacetsElement) return;
  //  document.querySelector(".active-facets-desktop").innerHTML =
  //    activeFacetsElement.innerHTML;

  //  FacetFiltersForm.toggleActiveFacets(false);
  //}
  static renderActiveFacets(html) {
    const source = html.querySelector(
      '.active-facets-desktop[data-type="filters"]'
    );
    if (!source) return;

    document
      .querySelectorAll('.active-facets-desktop[data-type="filters"]')
      .forEach((target) => {
        target.innerHTML = source.innerHTML;
      });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderShowItems(html) {
    const source = html.querySelector(
      '.active-facets-desktop[data-type="show-items"]'
    );
    if (!source) return;

    document
      .querySelectorAll('.active-facets-desktop[data-type="show-items"]')
      .forEach((target) => {
        target.innerHTML = source.innerHTML;
      });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  static handleHeroFilters() {
    const checkboxes = document.querySelectorAll(
      '#FacetFiltersByProductTypeForm .js-filter input[type="checkbox"]'
    );

    const tags = document.querySelectorAll(".tag-filters .js-hero-filter");

    const removebtn = document.querySelector(
      "#FacetFiltersByProductTypeForm .js-hero-filter-clear, .collection-hero__filters .js-hero-filter-clear"
    );

    if (!removebtn) return;

    let someSelected = false;

    if (checkboxes?.length) {
      someSelected = Array.from(checkboxes).some(
        (checkbox) => checkbox.checked
      );
    }
    if (tags?.length) {
      const parts = decodeURIComponent(location.pathname)
        .split("/")
        .filter(Boolean);

      const last = parts.at(-1) || "";
      const active = new Set(
        last
          .split("+")
          .filter(Boolean)
          .map((s) => s.toLowerCase())
      );

      tags.forEach((tag) => {
        const handle = (tag.dataset.tagHandle || tag.id || "").toLowerCase();
        tag.classList.toggle("active", active.has(handle));
      });

      Array.from(tags).some(
        (tag) => tag.classList.contains("active") && (someSelected = true)
      );
    }

    if (!removebtn) return;
    if (someSelected) {
      removebtn.classList.remove("disabled");
      removebtn.classList.remove("button--secondary");
      removebtn.classList.add("button--primary");
    } else {
      removebtn.classList.add("disabled");
      removebtn.classList.remove("button--primary");
      removebtn.classList.add("button--secondary");
    }

    removebtn.addEventListener("click", () => {
      Array.from(checkboxes).forEach((checkbox) => {
        checkbox.checked = false;
      });
      Array.from(tags).forEach((tag) => {
        tag.classList.remove("active");
      });
      removebtn.classList.add("disabled");
    });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );

    const forms = [];
    const currentForm = event.target.closest("form");
    sortFilterForms.forEach((form) => {
      if (
        form.id === "FacetSortForm" ||
        form.id === "FacetFiltersForm" ||
        form.id === "FacetSortDrawerForm" ||
        form.id === "FacetFiltersByProductTypeForm"
      ) {
        const noJsElements = document.querySelectorAll(".no-js-list");
        noJsElements.forEach((el) => el.remove());
        if (currentForm === form) {
          forms.push(this.createSearchParams(form));
        } else {
          const searchParamsWithoutProductType =
            this.createSearchParams(form)
              ?.split("&")
              ?.filter(
                (element) => !element.startsWith("filter.p.product_type")
              )
              ?.join("&") || "";

          forms.push(searchParamsWithoutProductType);
        }
      }
    });
    this.onSubmitForm(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    // §89 QA fix r1 (QA r5 nit): a filter chip or "Clear filters" in the bar
    // disappears when it is used, which dropped keyboard focus to <body>.
    // Remember its place in the row; renderFilters() then focuses the chip
    // that took that place, or "All filters" when the row is gone.
    const mgChip = event.currentTarget;
    if (
      document.body.classList.contains("template-collection") &&
      mgChip?.closest?.(".facets__top") &&
      (document.activeElement === mgChip ||
        mgChip.contains(document.activeElement))
    ) {
      const row = [
        ...mgChip
          .closest(".facets__top")
          .querySelectorAll("a.active-facets__button"),
      ];
      FacetFiltersForm.mgRefocus = { index: Math.max(0, row.indexOf(mgChip)) };
    }

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);

    const removebtn = document.querySelector(
      "#FacetFiltersByProductTypeForm .facets-tag-wrapper"
    );
    removebtn?.classList.add("active");
  }

  handleFacetButtonClick(event) {
    event.preventDefault();

    if (!this.facetButton || !this.facetForm) return;

    let labelHide = this.facetButton.querySelector(".hide-label");
    let labelShow = this.facetButton.querySelector(".show-label");
    let container = this.facetForm.querySelector(".facets__container");

    if (!container) return;

    // §89 r3: the filters the drawer opened with; closeFacets only moves the
    // page when they changed
    this.mgOpenParams = this.createSearchParams(this.facetForm);

    // §89 r5: the button that opened the drawer ("All filters" or a "Price"
    // shortcut), so closing hands focus back to it
    const mgFrom = document.activeElement;
    if (mgFrom?.matches?.(".facet-open-button, .facets-button-show")) {
      this.mgOpener = mgFrom;
    } else if (!container.classList.contains("active")) {
      this.mgOpener = null;
    }

    this.facetButton.classList.add("active");
    labelHide?.classList.remove("hidden");
    labelShow?.classList.add("hidden");
    container?.classList.add("active");
    document.body.classList.add("overflow-hidden");
    document.body.classList.add("filter-open");
    this.addEventListener(
      "transitionend",
      () => {
        trapFocus(this, this.querySelector('[role="dialog"]'));
      },
      { once: true }
    );
    //container.addEventListener("click", (event) => {
    //  if (container.querySelector(".facets__wrapper:hover") == null) {
    //    this.closeFacets(event);
    //  }
    //});
  }

  closeFacets(event) {
    event.preventDefault();

    if (!this.facetButton || !this.facetForm) return;

    let labelHide = this.facetButton.querySelector(".hide-label");
    let labelShow = this.facetButton.querySelector(".show-label");
    let container = this.facetForm.querySelector(".facets__container");

    this.facetButton?.classList.remove("active");
    labelHide?.classList.add("hidden");
    labelShow?.classList.remove("hidden");
    container?.classList.remove("active");

    document.body.classList.remove(`filter-open`);
    removeTrapFocus(this.focusElement);
    setTimeout(() => document.body.classList.remove("overflow-hidden"), 500);

    // §89 r4/r5: every way out of the drawer (X, Escape, the scrim, "Show
    // items") hands focus back to the button that opened it ("All filters"
    // or a filter shortcut such as "Price"), without moving the page; if a
    // re-render replaced that button, its successor (same filter index), else
    // "All filters". Before, Escape and "Show items" dropped focus to <body>.
    if (document.body.classList.contains("template-collection")) {
      let mgTo = this.mgOpener;
      this.mgOpener = null;
      if (mgTo && !mgTo.isConnected && mgTo.dataset.filterIndex) {
        mgTo = document.querySelector(
          `.facet-open-button[data-filter-index="${mgTo.dataset.filterIndex}"]`
        );
      }
      if (!mgTo || !mgTo.isConnected || !mgTo.getClientRects().length) {
        mgTo = [...document.querySelectorAll(".facets-button-show")].find(
          (b) => b.getClientRects().length
        );
      }
      mgTo?.focus({ preventScroll: true });
    }

    // §89 r3: the filters changed while the drawer was open and the shopper
    // is mid-grid: bring the first results back into view, just under the
    // sticky toolbar (and the header, which shows on scroll up). Collections
    // only; opening the drawer and closing it unchanged never moves the page.
    const mgOpened = this.mgOpenParams;
    this.mgOpenParams = undefined;
    const grid = document.getElementById("ProductGridContainer");
    if (
      mgOpened !== undefined &&
      document.body.classList.contains("template-collection") &&
      this.createSearchParams(this.facetForm) !== mgOpened &&
      grid &&
      grid.getBoundingClientRect().top < 0
    ) {
      const bar = document.querySelector(".collection-top");
      const header = document.querySelector(".shopify-section-header");
      const offset = (bar ? bar.offsetHeight : 0) + (header ? header.offsetHeight : 0) + 16;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({
        top: grid.getBoundingClientRect().top + window.scrollY - offset,
        behavior: reduce ? "auto" : "smooth",
      });
    }
  }

  onHandleKeyDown(event) {
    if (
      event.key === "Escape" &&
      this.facetWrapper?.classList.contains("active")
    ) {
      this.closeFacets(event);
    }
  }

  initCustomSortSelect() {
    const selectWrapper = this.querySelector(".facet-sort-select");
    const openButton = this.querySelector(".facet-sort-select__select");
    const closeButton = this.querySelector(".facet-sort-modal__close");
    const modal = this.querySelector(".facet-sort-modal");
    const modalInner = modal.querySelector(".facet-sort-modal__inner");
    const overlay = modal.querySelector(".facet-sort-modal__overlay");
    const customOptions = modal.querySelectorAll(".facet-sort-select__option");
    const hiddenInput = this.querySelector(".facet-sort-select__input");
    const currentValue = this.querySelector(".facet-sort-select__current");

    const openModal = () => {
      modal.classList.add("open");
      document.body.classList.add("overflow-hidden");
      trapFocus(modal, modalInner);
    };

    const closeModal = () => {
      modal.classList.remove("open");
      document.body.classList.remove("overflow-hidden");
      removeTrapFocus();
      openButton.focus();
    };

    openButton.addEventListener("click", () => {
      openModal();
    });

    openButton.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") openModal();
    });

    overlay.addEventListener("click", () => {
      closeModal();
    });

    closeButton.addEventListener("click", () => {
      closeModal();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal.classList.contains("open")) {
        closeModal();
      }
    });

    const onSelectOption = (e) => {
      const option = e.target.closest(".facet-sort-select__option");
      if (!option) return;

      const value = option.dataset.value;
      const text = option.textContent.trim();

      hiddenInput.value = value;
      this.facetForm.dispatchEvent(new Event("input"));
      currentValue.textContent = text;

      customOptions.forEach((o) =>
        o.classList.remove("facet-sort-select__option--selected")
      );
      option.classList.add("facet-sort-select__option--selected");

      closeModal();
    };

    customOptions.forEach((option) => {
      option.addEventListener("click", onSelectOption);
      option.addEventListener("keydown", (e) => {
        if (e.key === " " || e.key === "Enter") onSelectOption(e);
      });
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll(".field__input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") {
      maxInput.setAttribute("min", 0);
    }
    if (maxInput.value === "") {
      minInput.setAttribute("max", maxInput.getAttribute("max"));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = Number(inputRangeTo.max) - Number(inputRangeTo.min);
      const fromCurrent =
        Number(inputRangeFrom.value) - Number(inputRangeTo.min);
      const toCurrent = Number(inputRangeTo.value) - Number(inputRangeTo.min);
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        "style",
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll(".field__input")[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll(".field__input")[1];

      inputRangeFrom.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll(".field__range")[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll(".field__range")[1];

      inputNumberFrom.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to && from > to) {
          inputRangeFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to >= from) {
          inputRangeTo.value = to;
        } else {
          inputRangeTo.value = from;
        }

        this.fillSlider();
      };

      inputNumberFrom.onblur = () => {
        if (inputNumberFrom.value === "") return;
        const from = Number(inputNumberFrom.value);
        const min = Number(inputNumberFrom.getAttribute("min"));
        const max = Number(inputNumberFrom.getAttribute("max"));

        if (from < min) {
          inputRangeFrom.value = min;
          inputNumberFrom.value = min;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.onblur = () => {
        if (inputNumberFrom.value === "") return;
        const to = Number(inputNumberTo.value);
        const min = Number(inputNumberTo.getAttribute("min"));
        const max = Number(inputNumberTo.getAttribute("max"));

        if (to > max) {
          inputRangeTo.value = max;
          inputNumberTo.value = max;
        } else {
          inputRangeTo.value = to;
        }

        this.fillSlider();
      };
    });
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
